import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { TrashIcon, UserPlusIcon } from "@heroicons/react/24/outline";
import { toast } from "../../utils/toast";
import { Button, ConfirmModal, Input, Modal, RowAction, Table } from "../ui";
import { errorMessageOf, fieldErrorsOf, problemOf, scholarshipsApi } from "../../api/scholarships";
import { SCHOLARSHIPS_KEY, formatKigaliDate, fullName, staffScholarshipKey } from "./model";
import { canManageScholarships } from "./access";

export const evaluatorsKey = (id) => [...SCHOLARSHIPS_KEY, "evaluators", id];
const ACCOUNTS_KEY = [...SCHOLARSHIPS_KEY, "evaluator-accounts"];
const EMPTY_ACCOUNT = { firstName: "", lastName: "", email: "" };

// "Name (email)", or just the email when the account has no name
const accountLabel = (a) => (a.email && fullName(a) !== a.email ? `${fullName(a)} (${a.email})` : fullName(a));

// A new HEC evaluator account; they get an email to set up their sign-in
function NewEvaluatorDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState(EMPTY_ACCOUNT);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  const close = () => {
    setForm(EMPTY_ACCOUNT);
    setErrors({});
    onClose();
  };

  const submit = async (e) => {
    e.preventDefault();
    const found = {};
    if (!form.firstName.trim()) found.firstName = "First name is required";
    if (!form.lastName.trim()) found.lastName = "Last name is required";
    if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(form.email.trim())) found.email = "Enter a valid email address";
    if (Object.keys(found).length) return setErrors(found);
    setSaving(true);
    try {
      const { data } = await scholarshipsApi.post("admin/evaluators", { firstName: form.firstName.trim(), lastName: form.lastName.trim(), email: form.email.trim() });
      if (data.setUpEmailSent) toast.success(`${fullName(data)} can now be assigned`);
      else toast.warning(`${fullName(data)} can now be assigned, but the set-up email could not be sent. Send it again from Keycloak.`, { autoClose: 8000 });
      onCreated(data);
      close();
    } catch (error) {
      console.error("Failed to create an evaluator account:", error);
      const problem = problemOf(error);
      setErrors(problem?.code === "account-exists" ? { email: "Someone already has an account with this email" } : fieldErrorsOf(problem));
      toast.error(errorMessageOf(error, "Failed to create the account. Please try again."));
    } finally {
      setSaving(false);
    }
  };

  const field = (name) => ({
    value: form[name],
    onChange: (e) => {
      setErrors({});
      setForm((prev) => ({ ...prev, [name]: e.target.value }));
    },
    error: errors[name],
    disabled: saving,
  });

  return (
    <Modal
      open={open}
      onClose={close}
      title="New evaluator"
      footer={
        <>
          <Button variant="ghost" onClick={close} disabled={saving}>
            Cancel
          </Button>
          <Button type="submit" form="new-evaluator" loading={saving}>
            Create account
          </Button>
        </>
      }
    >
      <form id="new-evaluator" onSubmit={submit} className="grid gap-4 sm:grid-cols-2">
        <Input label="First name" required maxLength={100} {...field("firstName")} />
        <Input label="Last name" required maxLength={100} {...field("lastName")} />
        <Input className="sm:col-span-2" type="email" label="Work email" required maxLength={254} {...field("email")} />
      </form>
    </Modal>
  );
}

// Chooses several HEC staff at once, or all of them, to assign
function AddEvaluatorsDialog({ open, accounts, initial, busy, onClose, onAssign }) {
  const [selected, setSelected] = useState(initial);
  const allSelected = accounts.length > 0 && selected.length === accounts.length;
  const toggle = (id) => setSelected((prev) => (prev.includes(id) ? prev.filter((v) => v !== id) : [...prev, id]));

  return (
    <Modal
      open={open}
      onClose={onClose}
      dismissible={!busy}
      title="Add evaluators"
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={busy}>
            Cancel
          </Button>
          <Button icon={<UserPlusIcon className="h-4 w-4" />} onClick={() => onAssign(selected)} disabled={!selected.length} loading={busy}>
            {selected.length ? `Assign ${selected.length}` : "Assign"}
          </Button>
        </>
      }
    >
      {accounts.length === 0 ? (
        <p className="text-sm text-slate-600">Every evaluator account is already on the committee. Create a new one to add someone else.</p>
      ) : (
        <div className="flex flex-col gap-3">
          <label className="flex items-center gap-2 border-b border-slate-100 pb-3 text-sm font-semibold text-slate-700">
            <input type="checkbox" className="h-4 w-4 accent-brand" checked={allSelected} onChange={() => setSelected(allSelected ? [] : accounts.map((a) => a.principalId))} disabled={busy} />
            Select all ({accounts.length})
          </label>
          <div className="flex max-h-96 flex-col gap-2 overflow-y-auto">
            {accounts.map((a) => (
              <label key={a.principalId} className="flex items-center gap-2 rounded-md border border-slate-200 px-3 py-2 text-sm text-slate-700 has-[:checked]:border-brand has-[:checked]:bg-brand/5">
                <input type="checkbox" className="h-4 w-4 accent-brand" checked={selected.includes(a.principalId)} onChange={() => toggle(a.principalId)} disabled={busy} />
                {accountLabel(a)}
              </label>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
}

const plural = (n, one, many = `${one}s`) => `${n} ${n === 1 ? one : many}`;

/**
 * Who marks this scholarship's applications. Every assigned evaluator marks every submitted
 * application they have consented to, unless they remove it for a conflict of interest.
 */
export default function EvaluatorsTab({ scholarship }) {
  const queryClient = useQueryClient();
  const [adding, setAdding] = useState(null); // { initial: [principalId] } while choosing
  const [busy, setBusy] = useState(false);
  const [creating, setCreating] = useState(false);
  const [removing, setRemoving] = useState(null);
  const [reopen, setReopen] = useState(null); // { ids, flags, reviewed } waiting for confirmation
  const [override, setOverride] = useState(null); // { ids, flags } of HEC Admins, waiting for confirmation

  const { data: assigned = [], isPending } = useQuery({
    queryKey: evaluatorsKey(scholarship.id),
    queryFn: async () => (await scholarshipsApi.get(`admin/scholarships/${scholarship.id}/evaluators`)).data,
  });
  const { data: accounts = [] } = useQuery({
    queryKey: ACCOUNTS_KEY,
    queryFn: async () => (await scholarshipsApi.get("admin/evaluators", { params: { page: 0, size: 100 } })).data?.content ?? [],
  });

  const editable = canManageScholarships() && ["DRAFT", "PUBLISHED", "CLOSED"].includes(scholarship.state);
  const assignedIds = new Set(assigned.map((e) => e.principalId));
  const available = accounts.filter((a) => !assignedIds.has(a.principalId));
  const nameOf = (id) => accountLabel(accounts.find((a) => a.principalId === id) ?? { email: id });

  const refresh = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: evaluatorsKey(scholarship.id) }),
      queryClient.invalidateQueries({ queryKey: staffScholarshipKey(scholarship.id) }),
    ]);

  /**
   * Assigns each chosen person in turn. Those the API wants confirmed (reviewed applications go back
   * into review, or an HEC Admin marks) wait for one confirmation for all of them; `flags` carries
   * the confirmations given so far, so a second prompt keeps the first answer.
   */
  const assign = async (ids, flags = {}) => {
    setBusy(true);
    setReopen(null);
    setOverride(null);
    const needReopen = [];
    const needOverride = [];
    let assigned = 0;
    let reviewed = 0;
    try {
      for (const principalId of ids) {
        try {
          await scholarshipsApi.post(`admin/scholarships/${scholarship.id}/evaluators`, { principalId, ...flags });
          assigned += 1;
        } catch (error) {
          const problem = problemOf(error);
          if (problem?.code === "reopen-confirmation-required") {
            needReopen.push(principalId);
            reviewed = problem.body.reviewedApplications;
          } else if (problem?.code === "admin-override-required") needOverride.push(principalId);
          else {
            console.error(`Failed to assign evaluator ${principalId}:`, error);
            toast.error(`${nameOf(principalId)}: ${errorMessageOf(error, "could not be assigned. Please try again.")}`);
          }
        }
      }
      if (assigned) toast.success(`${plural(assigned, "evaluator")} assigned`);
      setAdding(null);
      // Those an HEC Admin override waits for are retried with the reopen too, then asked about
      if (needReopen.length) setReopen({ ids: [...needReopen, ...needOverride], flags, reviewed });
      else if (needOverride.length) setOverride({ ids: needOverride, flags });
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await scholarshipsApi.delete(`admin/scholarships/${scholarship.id}/evaluators/${removing.principalId}`);
      toast.success(`${fullName(removing)} removed`);
      setRemoving(null);
      await refresh();
    } catch (error) {
      console.error(`Failed to remove evaluator ${removing.principalId}:`, error);
      toast.error(errorMessageOf(error, "Failed to remove the evaluator. Please try again."));
    } finally {
      setBusy(false);
    }
  };

  const columns = [
    { key: "name", label: "Evaluator", accessor: fullName, render: (e) => <span className="font-medium text-slate-800">{fullName(e)}</span> },
    { key: "assignedAt", label: "Assigned", accessor: (e) => formatKigaliDate(e.assignedAt), sortValue: (e) => e.assignedAt },
    { key: "submittedCount", label: "Marked" },
    { key: "draftCount", label: "In progress" },
    { key: "conflictCount", label: "Conflicts declared" },
    ...(editable ? [{ key: "remove", label: "", exportable: false, render: (e) => <RowAction Icon={TrashIcon} label="Remove" variant="ghost" onClick={() => setRemoving(e)} disabled={busy} /> }] : []),
  ];

  return (
    <div className="flex flex-col gap-5">

      {editable && (
        <div className="flex flex-wrap justify-end gap-3">
          <Button icon={<UserPlusIcon className="h-4 w-4" />} onClick={() => setAdding({ initial: [] })} disabled={busy}>
            Add evaluators
          </Button>
          <Button variant="outline" onClick={() => setCreating(true)} disabled={busy}>
            New evaluator
          </Button>
        </div>
      )}

      <Table columns={columns} data={assigned} isLoading={isPending} searchable={false} exportable={false} emptyTitle="No committee members assigned yet" />

      <NewEvaluatorDialog
        open={creating}
        onClose={() => setCreating(false)}
        onCreated={async (account) => {
          await queryClient.invalidateQueries({ queryKey: ACCOUNTS_KEY });
          setAdding({ initial: [account.principalId] });
        }}
      />
      {adding && <AddEvaluatorsDialog open accounts={available} initial={adding.initial} busy={busy} onClose={() => setAdding(null)} onAssign={(ids) => assign(ids)} />}
      <ConfirmModal
        open={Boolean(removing)}
        onClose={() => setRemoving(null)}
        onConfirm={remove}
        loading={busy}
        destructive
        title={`Remove ${removing ? fullName(removing) : ""}?`}
        description="Their marks no longer count towards the scores. Applications they had not marked stop waiting for them."
        confirmLabel="Remove"
      />
      <ConfirmModal
        open={Boolean(reopen)}
        onClose={() => setReopen(null)}
        onConfirm={() => assign(reopen.ids, { ...reopen.flags, confirmReopen: true })}
        loading={busy}
        title="Send reviewed applications back into review?"
        description={`${reopen?.reviewed} application${reopen?.reviewed === 1 ? " is" : "s are"} already fully marked. ${
          reopen?.ids.length === 1 ? "The new evaluator" : `The ${reopen?.ids.length} new evaluators`
        } must mark ${reopen?.reviewed === 1 ? "it" : "them"} too before the award list can be finalised.`}
        confirmLabel="Assign and reopen"
      />
      <ConfirmModal
        open={Boolean(override)}
        onClose={() => setOverride(null)}
        onConfirm={() => assign(override.ids, { ...override.flags, adminOverride: true })}
        loading={busy}
        title={override?.ids.length > 1 ? "Assign HEC Admins as evaluators?" : "Assign an HEC Admin as evaluator?"}
        description={`${override?.ids.map(nameOf).join(", ")} ${override?.ids.length > 1 ? "are" : "is"} also an HEC Admin. If they mark applications, another HEC Admin must finalise the award list. The assignment is recorded in the history.`}
        confirmLabel="Assign anyway"
      />
    </div>
  );
}
